import {
  STANDALONE_FETAL_SEX,
  StandaloneFetalSex,
} from "../../../recoil/types";
import { parseLegacyExternalFetalSexEmailValue } from "./legacyExternalFetalSexEmailValue";

export const standaloneFetalSexes = Object.values(STANDALONE_FETAL_SEX);

const standaloneFetalSexByValue = new Map<string, StandaloneFetalSex>(
  standaloneFetalSexes.map((fetalSex) => [fetalSex, fetalSex]),
);

const isOneOf = <T extends string>(
  values: readonly T[],
  value: string,
): value is T => (values as readonly string[]).includes(value);

/**
 * Parses the fetal sex payload encoded in external reveal emails. New emails use
 * StandaloneFetalSex values; older immutable links fall back through the isolated
 * legacy compatibility map.
 */
export const parseExternalFetalSexEmailValue = (
  raw: string,
): StandaloneFetalSex | undefined => {
  const value = raw.trim();
  return (
    standaloneFetalSexByValue.get(value) ??
    parseLegacyExternalFetalSexEmailValue(value)
  );
};

export const twinStandaloneFetalSexes = [
  STANDALONE_FETAL_SEX.MALE_MALE,
  STANDALONE_FETAL_SEX.FEMALE_FEMALE,
  STANDALONE_FETAL_SEX.MALE_FEMALE,
  STANDALONE_FETAL_SEX.ONE_OR_BOTH_MALES,
] as const satisfies readonly StandaloneFetalSex[];

export type TwinStandaloneFetalSex = (typeof twinStandaloneFetalSexes)[number];

export const isTwinStandaloneFetalSex = (
  fetalSex: StandaloneFetalSex,
): fetalSex is TwinStandaloneFetalSex =>
  isOneOf(twinStandaloneFetalSexes, fetalSex);

const singletonStandaloneFetalSexes = [
  STANDALONE_FETAL_SEX.MALE,
  STANDALONE_FETAL_SEX.FEMALE,
] as const satisfies readonly StandaloneFetalSex[];

export const fetalSexResultBlockValues = [
  ...singletonStandaloneFetalSexes,
  ...twinStandaloneFetalSexes,
] as const satisfies readonly StandaloneFetalSex[];

export type FetalSexResultBlockValue =
  (typeof fetalSexResultBlockValues)[number];

export const getFetalSexResultBlockValue = (
  fetalSex?: StandaloneFetalSex,
): FetalSexResultBlockValue | undefined =>
  fetalSex !== undefined && isOneOf(fetalSexResultBlockValues, fetalSex)
    ? fetalSex
    : undefined;
